"use client";

import { useState, useSyncExternalStore } from "react";
import { useTranslations } from "next-intl";
import { track } from "@/lib/analytics";

type WindowWithGTM = Window & {
  dataLayer: unknown[];
  gtag?: (...args: unknown[]) => void;
};

function pushConsent(granted: boolean) {
  const value = granted ? "granted" : "denied";
  const win = window as unknown as WindowWithGTM;
  win.dataLayer = win.dataLayer || [];
  win.gtag?.("consent", "update", {
    analytics_storage: value,
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
  });
}

// useSyncExternalStore is the React-idiomatic way to read browser-only storage
// without causing hydration mismatches.
function subscribeNoop() {
  return () => {};
}

export default function ConsentBanner() {
  const t = useTranslations("consent");
  const [dismissed, setDismissed] = useState(false);

  const consent = useSyncExternalStore(
    subscribeNoop,
    () => localStorage.getItem("cookie_consent"),
    () => "ssr" // server snapshot — non-null keeps banner hidden during SSR
  );

  // null means key absent (first visit) → show banner
  const show = consent === null && !dismissed;

  function accept() {
    localStorage.setItem("cookie_consent", "granted");
    pushConsent(true);
    track("consent_granted");
    setDismissed(true);
  }

  function decline() {
    localStorage.setItem("cookie_consent", "declined");
    pushConsent(false);
    track("consent_denied");
    setDismissed(true);
  }

  if (!show) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6">
      <div className="max-w-6xl mx-auto bg-[var(--surface)] border border-white/10 rounded-lg px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <p className="text-sm text-[var(--muted)] flex-1">
          {t("message")}{" "}
          <span className="text-[var(--text)]">{t("noPersonalData")}</span>
        </p>
        <div className="flex items-center gap-4 shrink-0">
          <button
            onClick={decline}
            className="text-sm min-h-[44px] px-4 py-2 border border-white/10 rounded text-[var(--muted)] hover:text-[var(--text)] hover:border-white/30 transition-colors"
          >
            {t("decline")}
          </button>
          <button
            onClick={accept}
            className="text-sm min-h-[44px] px-4 py-2 bg-[var(--accent)] text-[var(--on-accent)] rounded font-medium hover:opacity-90 transition-opacity"
          >
            {t("accept")}
          </button>
        </div>
      </div>
    </div>
  );
}
